/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * WHAT A CLIENT LINK MAY READ OF THE REPORT AND COLLECTED DATA (item d).
 *
 * Before this, /api/client-read sent the report column and collected_data to
 * the client's browser whole: the risk tier and its rationale, the risk
 * factors, red flags, the screening records and their resolutions, the
 * decision record, the inconsistency flags, the delegate tokens, a full copy
 * of the board (collected_data.documentsBoard) and its brief. The portal held
 * them because it built Alex's system prompt itself. The prompt is now built
 * on the server (api/_client-alex.js), so the browser is sent only what the
 * portal's own screens and flows read. Every client route takes its
 * projection from here; nothing else decides what a client sees.
 *
 * The lists are ALLOWLISTS. A key that is not named here never leaves the
 * server on a client route, whatever its name, so a new officer or engine key
 * is hidden by default. Each entry says which screen or flow reads it.
 *
 * Writes (api/update-session.js, client path): a key the client cannot read
 * keeps the server's value on every client write (clientCollectedWrite), so a
 * browser holding only this projection can never erase or replace a hidden
 * key by writing its copy back. Pure functions only; no I/O.
 */
import { clientCarriedItems } from './request-items.js';
import { clientItemLabel } from './client-copy.js';
import { clientPointsOut, pageFixedPoints } from './funding-answer.js';
import { trustedCompanyName } from './board-allowlist.js';

// ── report: the fields the portal reads ──────────────────────────────────────
//   clientName   the documents first screens name the company from the board
//                (the submitted panel, "We have received ... the documents for
//                <clientName>"), and an assembled board (one with a clientName)
//                is the portal's durable "the review has finished" signal (the
//                board poll and the review state check, v1_642).
//                Second review B1 (22/10/2026): sent as clientBoardName, never
//                the board's string unless it matches the client's own typed
//                name or a register's legal name; otherwise the client's own
//                typed name, else a non empty fallback, so the board ready
//                signal still holds.
export const CLIENT_REPORT_FIELDS = Object.freeze(['clientName']);
export const CLIENT_NAME_FALLBACK = 'your company';
/**
 * The board's company name as a client (the page, Alex's greeting, the healed
 * chat state) may read it (trustedCompanyName, board-allowlist.js): the
 * board's name only when it matches the client's typed name or a register's
 * legal name in the case's collected data, else the client's typed name, else
 * CLIENT_NAME_FALLBACK. Never empty. A string in place of collected is taken
 * as the client's typed name.
 */
export function clientBoardName(board, collected) {
  const cd = typeof collected === 'string' ? { companyName: collected } : collected;
  return trustedCompanyName(board, cd).name || CLIENT_NAME_FALLBACK;
}

// ── collected_data: the keys the portal reads (and may write) ───────────────
// Screen or flow that reads each one, in src/App.jsx unless named.
export const CLIENT_COLLECTED_KEYS = Object.freeze([
  // who the client is (welcome, status panel, notify on upload, the SOW interview's name line)
  'contactName', 'contactEmail', 'companyName', 'inviteToken',
  // which machine the portal runs (invite load and return visit routing, both modes)
  'onboardingMode', 'inviteType', 'parentSessionId', 'sowReopened',
  // the welcome names the company from the pre screen (load-invite already sends it, v1_982)
  'preScreen',
  // where the staged interview got to (return visit stage restore) and the consent gate
  'stage', 'consentConfirmed', 'consentTimestamp',
  // the financial declaration step is skipped once given
  'financialRanges',
  // documents first intake: the upload manifest the portal re-fires a stalled run from,
  // the intake answers and when processing started (the stall test)
  'intake', 'docManifest', 'dfContext', 'dfProcessingStartedAt',
  // documents first chat state: which chat, whether the board is ready (the strip and the
  // pending poll), the points the greeting and the pending re-entry work from
  // (documentsFirstQuestions is sent rebuilt, clientPointsOut; server owned on a write)
  'documentsFirstChat', 'boardReady', 'documentsFirstQuestions',
  // the request round on a return visit (both modes): the working list, the durable list,
  // when it was sent, delivered, greeted and completed (postReturnGreetingOnce and the
  // re-entry tests)
  'pendingMlroQuestions', 'mlroRequestedQuestions', 'mlroRequestSentAt', 'mlroQuestionsDeliveredAt',
  'returnGreetedAt', 'mlroFollowupSubmittedAt',
  // submission state (the submitted family test, the completion latch, the SUBMIT gate)
  'submittedAt',
  // the soft close card and the checklist mirror (projected item by item, below), and the
  // pre submission review the soft close reads (projected, below)
  'requestItems', 'preSubmitReviewDone', 'preSubmitReview',
  // the firm's own questions: the client's answers show on the checklist card
  'firmQuestions',
  // wallets the client typed (the post submission wallet screen and the documents first write)
  'walletAddresses',
  // the live screening request is fired once per visit
  'liveScreeningRequested',
  // the upload notification throttle
  'updateNotifyAt',
  // the website step: the pre screen confirmed the site
  'websiteConfirmed',
  // Invite with documents (14/10/2026): the firm supplied documents before the
  // invitation, so the first visit shows "Documents we already have from you"
  // (read only: api/update-session.js keeps the stored value, CLIENT_LOCKED_KEYS)
  'prefiled',
  // Individual onboarding (21/10/2026): the individual interview's page (read
  // only; api/update-session.js keeps the stored value on every client write)
  'clientKind'
]);
// The invite load (api/load-invite.js) reads only these four, before the session
// read: which machine to run and the pre screen the welcome names (v1_982).
// Individual onboarding (21/10/2026): clientKind tells the page to run the
// individual interview. Read only: api/update-session.js keeps the stored value.
export const INVITE_COLLECTED_KEYS = Object.freeze(['onboardingMode', 'inviteType', 'parentSessionId', 'preScreen', 'clientKind']);
// The staged interview's own answers: '<stage>_response' (handleSend) and the fields the
// website step pre fills ('prefilled_*'), both the client's own words or site.
export const CLIENT_COLLECTED_PATTERNS = Object.freeze([/^prefilled_[A-Za-z]+$/, /^[a-z_]+_response$/]);

// Keys the browser WRITES but is never sent back: results the browser fetched itself
// in this visit (the website research, the public register checks), and the
// submission's analysis request that update-session turns into its own stamp (v1_861). On a client write
// an incoming value replaces the stored one; an absent one keeps the stored value.
export const CLIENT_WRITE_ONLY_KEYS = Object.freeze(['websiteData', 'externalVerifications', 'analysisRequested']);

// One request item, as the portal reads it (RequestItemsCard, softCloseItems, the
// checklist mirror, guardCompletionClaim through clientChecklistItems). The item's
// history keeps only its state moves (sentByOfficer reads them), never who or why.
const ITEM_FIELDS = ['id', 'type', 'label', 'theme', 'state', 'round', 'dueAt', 'note', 'askedAt', 'answeredAt', 'evidence', 'delegationRequested', 'mergedIds'];
export function clientItem(i) {
  if (!i || typeof i !== 'object') return i;
  const out = {};
  for (const f of ITEM_FIELDS) if (i[f] !== undefined) out[f] = i[f];
  // Fault 1: the client reads the client form of the label (client-copy.js).
  if (out.label !== undefined) out.label = clientItemLabel(i);
  if (i.owner && typeof i.owner === 'object') out.owner = i.owner.kind === 'delegate' ? { kind: 'delegate', name: i.owner.name } : { kind: i.owner.kind };
  if (Array.isArray(i.history)) out.history = i.history.filter(h => h && typeof h === 'object').map(h => ({ at: h.at, from: h.from, to: h.to }));
  return out;
}
// The pre submission review, as the portal reads it (the soft close and its trigger).
const REVIEW_FIELDS = ['checkedAt', 'questions', 'instruction', 'itemIds', 'readiness'];
export function clientReview(r) {
  if (!r || typeof r !== 'object') return r;
  const out = {};
  for (const f of REVIEW_FIELDS) if (r[f] !== undefined) out[f] = r[f];
  return out;
}

// The pre screen, as a client route returns it (rows 3 and 4, 19/10/2026). The
// portal's welcome names the company (companyName) and skips the greeting when
// the check did not complete (checkStatus, prescreen-status.js). The rest (the
// regulatory status, adverse news, red flags, notes, sources) is the engine's
// and the officer's research and never leaves the server on a client route.
// Used for collected_data.preScreen and for the pre_screen_data column.
export const CLIENT_PRESCREEN_FIELDS = Object.freeze(['companyName', 'checkStatus']);
export function clientPreScreen(ps) {
  if (!ps || typeof ps !== 'object' || Array.isArray(ps)) return null;
  return {
    companyName: typeof ps.companyName === 'string' ? ps.companyName : null,
    checkStatus: typeof ps.checkStatus === 'string' ? ps.checkStatus : null,
  };
}

// THE DOCUMENT VERDICT A CLIENT SEES (#246b, decided 20/10/2026). The reading's
// verdict is the platform's view of the document: "Unsatisfactory" covers
// "appears altered or forged" and "Requires Clarification" a doubt about it, so
// the client (their Alex, their page, their document list) never sees either.
// Satisfactory reads "Received"; Requires Clarification and Unsatisfactory both
// read as one neutral line. Pending and Superseded are not a view of the
// document and pass as they are. The officer's screens keep the verdict.
export const CLIENT_DOC_RECEIVED = 'Received';
export const CLIENT_DOC_NEEDS_COPY = 'We may need a clearer or more complete copy';
export function clientDocVerdict(assessment) {
  if (assessment == null) return null;
  const s = String(assessment).trim();
  const k = s.toLowerCase();
  if (!s) return null;
  if (k === 'satisfactory' || k === 'received') return CLIENT_DOC_RECEIVED;
  if (k === 'pending' || k === 'superseded') return s;
  return CLIENT_DOC_NEEDS_COPY;
}

// The key findings a client may read (fix-client-findings, 20/10/2026). A
// reading's key findings are meant to be plain facts from the document, but a
// reader could write its view of the document there ("the closing balance does
// not reconcile", "the font on page 2 differs"), and the platform writes its
// own officer wording there when a read fails ("Analysis failed", "not read by
// the platform"). Any such finding is withheld from every client view: the
// client's Alex, the page's live reading and the pre submission review. The
// recorded reading keeps every finding for the officer. withheld is a count for
// the server's log and the officer's record, never sent to the client.
const WITHHELD_FINDING = new RegExp('\\b(' + [
  'inconsisten\\w*', 'contradict\\w*', 'discrepan\\w*', 'mismatch\\w*',
  'do(?:es)? not (?:match|reconcile|agree|correspond|tally)', 'did not (?:match|reconcile|agree)', 'differs? from', 'different from',
  'altered', 'alterations?', 'forged', 'forgery', 'tamper\\w*', 'manipulat\\w*', 'doctored', 'edited',
  'fonts?', 'typefaces?', 'formatting',
  'illegible', 'unreadable', 'blurr\\w*', 'cropped', '(?:is|are) missing', 'missing (?:pages?|signatures?|sections?|dates?)', 'incomplete', 'partial copy',
  'unsigned', 'not signed', 'undated', 'not dated', 'expired', 'out of date', 'stale',
  'suspicio\\w*', 'irregular\\w*', 'anomal\\w*', 'not genuine', 'fraud\\w*', 'counterfeit',
  'cannot be verified', 'could not be verified', 'unable to verify', 'appears? to have been',
  'analysis failed', 'not read by the platform', 'could not be read', 'not been assessed', 'must be read directly',
].join('|') + ')\\b', 'i');
export function clientFindings(list) {
  const all = Array.isArray(list) ? list.filter(f => typeof f === 'string') : [];
  const kept = all.filter(f => !WITHHELD_FINDING.test(f));
  return { kept, withheld: all.length - kept.length };
}
/**
 * B2 (21/10/2026): model written prose a client view may carry (a board
 * narrative, a gap line, a digest of findings), sentence by sentence through the
 * same rule as key findings. Returns { text, withheld }: the sentences kept, in
 * order, and how many were left out. Not a string gives empty text.
 */
// Fresh review fixes (22/10/2026): words that, anywhere in a board value, are
// the compliance view (a crime, a screening result, a regulatory status, a
// verdict on the evidence). In prose they withhold the sentence (the board
// rule). Since the owner decision of 22/10/2026 (allowlist) the board rule is
// only a second backstop on what Alex reads (see boardWordsIn below).
const BOARD_STATUS_TERMS = [
  'sars?', 'suspicious activit\\w*', 'money launder\\w*', 'launder\\w*', 'proceeds of crime', 'peps?', 'politically exposed', 'sanction\\w*',
  'investigat\\w*', '(?:to|for|with|by) the mlro', 'refer\\w* (?:\\w+ )?to (?:the )?mlro', 'mlro (?:review|view|decision|referral|should|must|to|will|may|is minded|considers|sign)',
  'struck off', 'strike off', 'fake', 'forged', 'forgery', 'falsif\\w*', 'allege\\w*', 'alleg(?:ation|ations|edly)', 'brib\\w*', 'corrupt\\w*', 'fraud\\w*', 'terroris\\w*',
  'criminal\\w*', 'crimes?', 'convict\\w*', 'disqualif\\w*', 'cancel\\w*', 'revok\\w*', 'revocation', 'lapsed', 'unverified', 'unsatisfactory', 'weak\\w*',
  'blacklist\\w*', 'flagged', 'suspended', 'dissolved', 'liquidat\\w*', 'former minister', 'minister\\w*', 'shell[\\s-]like',
  'cash[\\s-]intensive', '(?:is|are|appears?|seems?) (?:complex|convoluted)', 'linked to', 'before onboarding', 'further information (?:is )?(?:needed|required)',
];
// The board's own assessment words (B2): a sentence that judges, doubts or
// weighs the client's account is the compliance view, whatever it is about.
const ASSESSMENT_WORDS = new RegExp('\\b(?:' + [
  'weak', 'unverified', 'not verified', 'cannot be verified', 'could not be verified', 'implausible', 'not plausible', 'insufficient',
  'unsupported', 'not supported', 'not evidenced', 'unevidenced', 'unexplained', 'unclear', 'doubt\\w*', 'concern\\w*', 'suspicio\\w*',
  'questionable', 'red flags?', '(?:high|higher|elevated|increased) risk', 'risk (?:rating|tier|view)', 'decline\\w*', 'reject\\w*', 'refus\\w*', 'satisfactory', 'unsatisfactory',
  'requires? clarification', 'assessed as', 'assessment', 'the board (?:considers|finds|notes|is)', 'the officer', 'officer:', 'compliance (?:team|view)', 'minded to', 'adverse',
  // Widened after two independent reviews of 21/10/2026: the strength of the
  // evidence, verdicts and scores, the firm's next steps, screening, regulatory
  // and typology findings. Each is narrowed so a plain business fact (a credit
  // scoring firm, the client's own MLRO, a date) still reaches Alex. A fact
  // that still matches is withheld and Alex asks for it (fails safe). This is
  // a denylist and cannot be complete; see the design note in the B2 summary.
  'weak\\w*', 'the mlro (?:should|must|to|may|will|is minded|considers)', 'mlro (?:review|view|decision)', 'edd', 'enhanced due diligence (?:applies|is required|required|warranted)',
  'scor(?:e|ed|ing)\\s*(?::\\s*|of\\s*|at\\s*)?\\d+', '^\\s*(?:request|ask|probe|obtain|challenge|verify)', 'no \\w+ (?:\\w+ )?(?:statement|documents?|evidence|letter) (?:was|were|has been|have been) (?:supplied|provided|seen)', 'scored (?:low|medium|high|elevated|amber|red|green)', 'rated (?:low|medium|high|elevated|amber|red|green)', 'risk (?:rating|level|score|is|:)',
  '(?:low|medium|moderate|high|higher|elevated|increased)[\\s-]risk', 'recommend\\w*', 'approv(?:e|al)\\b', 'sign(?:ed)? off', 'plausib\\w*', 'credib\\w*', 'corroborat\\w*', 'uncorroborated',
  '(?:partially|adequately|not yet|not fully|poorly|only partly|partly) (?:evidenced|verified|documented)', 'not (?:adequately|sufficiently|fully) (?:documented|evidenced|verified)',
  'verification (?:outstanding|incomplete|pending)', 'lack\\w*', 'thin', '(?:not |un)convincing', 'not satisfied', 'insufficien\\w*', 'not sufficient', 'sufficiency', '(?:not |un)acceptable', 'hard to verify', 'no evidence',
  'not (?:yet )?(?:explained|disclosed|provided|confirmed|supplied)', '(?:was|were|has|have) not (?:been )?(?:provided|supplied|evidenced|explained)', 'no supporting (?:documentation|documents?|evidence)',
  'undisclosed', 'unconfirmed', 'do(?:es)? not add up', 'out of line', 'material gap', 'gap:', 'confidence', 'consistent with', 'reconcil\\w*', 'verdict', 'outcome', 'conclusion', 'established',
  'rag', 'amber', 'should be (?:followed up|withheld|reviewed|escalated)', 'escalat\\w*', 'appears? (?:high|low|inconsistent|excessive)', 'changed between', 'different (?:explanations|accounts)',
  'onboarding (?:can|cannot|should|may|must)', '(?:should|must|can|cannot) (?:not )?proceed', 'challeng\\w*', 'do not (?:mention|tell|disclose)', 'tip\\w* off',
  'sanctioned', 'sanctions? (?:list|exposure|risk|hit|match)', 'pep (?:hit|match|status|exposure)', '(?:a|the|possible|potential|likely) pep', 'grade [a-e]', 'graded', 'politically exposed', 'fatf', 'grey list', 'layering', 'structuring (?:of deposits|pattern|of cash)', 'typolog\\w*',
  'hits?', 'watch ?list', 'ofac', 'minister', 'opaque', 'obscur\\w*', 'could not be (?:traced|established|confirmed|verified)', 'nominee', 'exposure', 'shell compan\\w*',
  'cancel(?:led|ed)', 'lapsed', 'revoked', 'disqualif\\w*', 'not on the \\w+ register', 'without (?:the required )?authori[sz]ation', 'voluntary requirement', 'imposed a fine', 'fined',
  'older than', 'months old', 'expir\\w*', 'atypical', 'unusual', 'disproportionate', 'round sum', 'rapid movement',
  // Review fixes (21/10/2026): the negative prefix forms, which \b cannot find
  // inside a longer word ("implausibly" holds no word boundary before
  // "plausib"), and three more judgement words. 'incredib' also withholds plain praise
  // ("an incredible year"), which fails safe.
  'implausib\\w*', 'uncredib\\w*', 'incredib\\w*', 'unsubstantiat\\w*', 'not substantiated', 'unverif\\w*', 'unreliab\\w*', 'inadequa\\w*',
  'dubious', 'unlicen[cs]ed', 'a front for',
  // Fresh review fixes (22/10/2026): crime, screening and status words that
  // reached Alex ("Money laundering risk.", "We will file a SAR.", "Company
  // struck off in 2021."). The MLRO is withheld only where the sentence sends
  // something to the MLRO or speaks of the MLRO's view, so the client's own
  // MLRO named as a fact ("John Smith is the MLRO") still reaches Alex.
  ...BOARD_STATUS_TERMS,
].join('|') + ')\\b', 'i');
// A part that is nothing but a verdict ("Medium.", "4/10") is withheld too.
const LONE_VERDICT = /^\s*(?:[\w ]{1,30}:\s*)?(?:(?:low|medium|moderate|high|elevated|standard|enhanced|amber|red|green|pass|fail|refer|partial)\.?|\d+\s*(?:\/|out of)\s*\d+\.?)\s*$/i;
// A score ("4/10", "7 out of 10"), never a date ("12/10/2019", "3/5/2018").
const SCORE_FORM = /(?<![\d/]\s*)\b\d+\s*(?:\/|out of)\s*(?:5|10|100)\b(?!\s*\/\s*\d)/i;
// Owner decision 22/10/2026 (B2, option 1 allowlist): Alex reads no board free
// text at all. What Alex reads from the board is built from structured facts
// only (src/shared/board-allowlist.js). The board rule below (the denylist of
// assessment words) is kept ONLY as a second backstop on what that allowlist
// leaves; nothing depends on it.
const ASSESSMENT_WORDS_ALL = new RegExp(ASSESSMENT_WORDS.source, 'gi');
/** Backstop only: the listed words in a text, each with where it starts. */
export function boardWordsIn(text) {
  if (typeof text !== 'string' || !text) return [];
  const out = [];
  for (const m of text.matchAll(ASSESSMENT_WORDS_ALL)) out.push({ word: m[0], index: m.index });
  return out;
}
/** Backstop only: the board rule on one free text value, '' when nothing may go. */
export function boardText(text) {
  if (typeof text !== 'string') return '';
  return clientProse(text.replace(/\s+/g, ' ').trim(), { board: true }).text.replace(/[;,]\s*$/, '').trim();
}
export function clientProse(text, { board = false } = {}) {
  if (typeof text !== 'string' || !text.trim()) return { text: '', withheld: 0 };
  // The board's prose also joins a fact and its judgement with a spaced dash.
  const parts = (board ? text.replace(/\s+[\u2012\u2013\u2014\u2015-]+\s+/g, '; ') : text).split(/(?<=[.;!?])\s+(?=\S)/);
  let { kept } = clientFindings(parts);
  if (board) kept = kept.filter(p => !ASSESSMENT_WORDS.test(p) && !LONE_VERDICT.test(p) && !SCORE_FORM.test(p));
  return { text: kept.join(' ').trim(), withheld: parts.length - kept.length };
}
/** How many of a reading's key findings no client view carries. */
export function withheldFindings(r) {
  return clientFindings(r && typeof r === 'object' ? r.keyFindings : null).withheld;
}

// A document reading, as the interview reader returns it to the client's browser
// (#239, 19/10/2026). The page names the document (docType), shows its state
// (assessment) and what was found (keyFindings), and lists the people a register
// names (registerExtract). The flags, the informationals and every other field
// stay on the case for the officer. A field of the wrong shape is sent empty.
// #246b: the state is the client form of the verdict (clientDocVerdict).
// fix-client-findings: the key findings are the ones a client may read
// (clientFindings).
export const CLIENT_READING_FIELDS = Object.freeze(['docType', 'assessment', 'keyFindings', 'registerExtract']);
export function clientReading(r) {
  const src = r && typeof r === 'object' ? r : {};
  return {
    docType: typeof src.docType === 'string' ? src.docType : null,
    assessment: typeof src.assessment === 'string' ? clientDocVerdict(src.assessment) : null,
    keyFindings: clientFindings(src.keyFindings).kept,
    registerExtract: src.registerExtract && typeof src.registerExtract === 'object' && !Array.isArray(src.registerExtract) ? src.registerExtract : null,
  };
}

export function isClientCollectedKey(k) {
  return CLIENT_COLLECTED_KEYS.includes(k) || CLIENT_COLLECTED_PATTERNS.some(re => re.test(k));
}
export function isClientWritableKey(k) {
  return isClientCollectedKey(k) || CLIENT_WRITE_ONLY_KEYS.includes(k);
}

/** The report as a client route returns it. null stays null. */
export function clientReport(report, { collected = null, fallbackName } = {}) {
  if (!report || typeof report !== 'object') return report == null ? report : null;
  const out = {};
  for (const f of CLIENT_REPORT_FIELDS) if (report[f] !== undefined) out[f] = report[f];
  // A truthy clientName is the page's board ready signal: it stays truthy, cut.
  if (out.clientName) out.clientName = clientBoardName(report, collected || (fallbackName ? { companyName: fallbackName } : null));
  return out;
}

/**
 * collected_data as a client route returns it: request items the officer has not
 * put to the client are dropped (v1_921, clientCarriedItems), then only the
 * allowlisted keys, with items and the review projected.
 */
export function clientCollected(cd) {
  if (!cd || typeof cd !== 'object') return cd == null ? cd : {};
  const out = {};
  for (const [k, v] of Object.entries(cd)) {
    if (!isClientCollectedKey(k)) continue;
    if (k === 'requestItems') out[k] = Array.isArray(v) ? clientCarriedItems(v, cd).map(clientItem) : v;
    else if (k === 'preSubmitReview') out[k] = clientReview(v);
    else if (k === 'preScreen') out[k] = clientPreScreen(v);
    // B2 (22/10/2026): the points to cover leave the server rebuilt, through the
    // allowlist Alex's prompt uses, never in the board's own wording.
    else if (k === 'documentsFirstQuestions') out[k] = clientPointsOut(v, cd.documentsBoard || null);
    else out[k] = v;
  }
  return out;
}

/** The keys a client projection leaves out (for a harness or a log, never a response). */
export function hiddenCollectedKeys(cd) {
  return Object.keys((cd && typeof cd === 'object') ? cd : {}).filter(k => !isClientCollectedKey(k));
}

/**
 * A CLIENT WRITE OF collected_data, MERGED SO NOTHING HIDDEN IS LOST.
 * The browser holds only clientCollected(stored), and the portal writes
 * collected_data whole, so before this a key the browser was not sent would be
 * erased by its next write. Returns the object to store and the hidden keys the
 * write carried that the store already holds (for the server log; never sent
 * back, so the answer is no test of what the store holds):
 *   - a key the client may read: the incoming value when sent; a key the write
 *     leaves out keeps the stored value (a partial write, such as the pre
 *     screen's { preScreen }, never clears the rest of the record);
 *   - a write only key: the incoming value when sent, else the stored one;
 *   - a hidden key the store holds: the stored value always stands;
 *   - a hidden key the store does not hold: taken (the client's own new key,
 *     as before; the locks in update-session still drop the engine's inputs);
 *   - the request items, the pre submission review and the pre screen are
 *     projections in the browser: the stored values stand (update-session
 *     already holds them), so the browser's cut down pre screen can never
 *     replace the stored one;
 *   - the points to cover (documentsFirstQuestions) are SERVER OWNED (B1
 *     review fix, 22/10/2026): the stored list always stands, so a client
 *     write never grows, empties, reorders or rewords it (the page holds a
 *     rebuilt copy and builds its own lists from it). With nothing stored the
 *     page may store only its own fixed questions (pageFixedPoints: the
 *     platform's and its activity questions), never other text.
 * The existing locks in update-session (engine owned, officer keys, firm
 * questions, the stamps) run after this, unchanged.
 */
export function clientCollectedWrite(stored, incoming) {
  const server = (stored && typeof stored === 'object') ? stored : {};
  const inc = (incoming && typeof incoming === 'object') ? incoming : {};
  const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
  const out = { ...server };
  const ignored = [];
  for (const [k, v] of Object.entries(inc)) {
    if (k === 'requestItems' || k === 'preSubmitReview' || k === 'preScreen') { if (!has(server, k)) out[k] = v; continue; }
    // The server's record of the officer note on what Alex does not read is never
    // a client's to set (defence in depth beside CLIENT_LOCKED_KEYS in update-session).
    if (k === 'alexPointsWithheld') { if (has(server, k)) out[k] = server[k]; else delete out[k]; continue; }
    // The points to cover are server owned: the stored list stands.
    if (k === 'documentsFirstQuestions') { if (!has(server, k)) { const own = pageFixedPoints(v); if (own.length) out[k] = own; } continue; }
    if (isClientWritableKey(k)) { out[k] = v; continue; }
    if (has(server, k)) { ignored.push(k); continue; }
    out[k] = v;
  }
  return { collected: out, ignored };
}

/**
 * A CLIENT WRITE OF THE REPORT, BY ALLOWLIST (hotfix 29/10/2026, owner decision).
 * It was a merge, { ...stored, ...incoming }, so a client link could overwrite any
 * analysis field it sent (riskTier, riskAssessment, mlroRecommendation, the red
 * flags, the people). The portal writes a report in two places only, both on the
 * source of wealth interview (src/App.jsx generateSowReport):
 *   'sow_own'     the interview's own result, on the interview case: only the
 *                 summary keys the source of wealth prompt returns (SOW_REPORT_KEYS);
 *   'sow_parent'  the parent case, written with the link of a source of wealth
 *                 interview whose parent is this case: only the sow* fields
 *                 (SOW_UBO_FIELDS) of the one owner that interview was sent to
 *                 (owner rule below). Nothing else of the parent's report, no
 *                 other owner, and no new entry.
 * Any other client write of the report ('none') keeps the stored report whole.
 * Returns { report, refused }: refused names every key the write could not set.
 *
 * THE OWNER RULE (29/10/2026, owner): the interviewed owner is found by the
 * interview case's contact email, then name. An email is used only when exactly
 * one owner on the case holds it. When two owners share it (NextTrade: both
 * Mortons stamped by each interview), the name decides among them, and when the
 * name does not settle it either, no owner is stamped and the write is refused.
 * Each incoming owner entry is placed on a stored owner by the same rule.
 */
export const SOW_REPORT_KEYS = ['uboName', 'companyName', 'wealthOrigins', 'shareholdingOrigin', 'professionalBackground', 'wealthTimeframe', 'significantEvents', 'wealthStructure', 'evidenceProvided', 'evidenceType', 'sowAssessment', 'sowNotes', 'mlroActions', 'mlroPriority', 'interviewComplete', 'interviewDate', 'sowReportIncomplete'];
export const SOW_UBO_FIELDS = ['sowNarrative', 'sowTimeframe', 'sowSignificantEvents', 'sowStructure', 'sowEvidence', 'sowProfessionalBackground', 'sowAssessment', 'sowReportIncomplete', 'sowInterviewComplete', 'sowInterviewSent', 'sowInterviewDate'];
const lc = (v) => String(v || '').trim().toLowerCase();
// Key order never matters (jsonb hands keys back in its own order), so values
// are compared with their object keys sorted, at every depth.
const canon = (v) => (Array.isArray(v) ? v.map(canon)
  : (v && typeof v === 'object') ? Object.keys(v).sort().reduce((o, k) => { o[k] = canon(v[k]); return o; }, {})
  : v);
export const sameValue = (a, b) => JSON.stringify(canon(a === undefined ? null : a)) === JSON.stringify(canon(b === undefined ? null : b));
// A key the write merely echoes back unchanged is not a write attempt.
const differs = (a, b) => !sameValue(a, b);
// The owner rule: the one stored owner a person (email, name) is, or null.
// 29/10/2026 (owner, next release): every owner stamp (the server's and both
// dashboard stamps) uses this rule. An owner holds an email when it is the
// owner's own or the address their interview was sent to (sowInterviewEmail).
// A caller may pass its own name test (the dashboard and server pass samePerson,
// which ignores titles and spacing); the default is an exact match, ignoring case.
export function resolveOwner(owners, who, { sameName } = {}) {
  const list = (Array.isArray(owners) ? owners : []).filter(e => e && typeof e === 'object');
  const email = lc(who && who.email), whoName = who && who.name;
  const nameEq = typeof sameName === 'function' ? sameName : (a, b) => !!lc(a) && lc(a) === lc(b);
  const byName = (pool) => { const m = lc(whoName) ? pool.filter(e => e.name && nameEq(e.name, whoName)) : []; return m.length === 1 ? m[0] : null; };
  const byEmail = email ? list.filter(e => lc(e.email) === email || lc(e.sowInterviewEmail) === email) : [];
  if (byEmail.length === 1) return byEmail[0];
  if (byEmail.length > 1) return byName(byEmail);
  return byName(list);
}
export function clientReportWrite(storedReport, incoming, { mode = 'none', interviewee = null } = {}) {
  const s = (storedReport && typeof storedReport === 'object') ? storedReport : {};
  const inc = (incoming && typeof incoming === 'object') ? incoming : {};
  const out = { ...s };
  const refused = [];
  if (mode === 'sow_own') {
    for (const [k, v] of Object.entries(inc)) { if (SOW_REPORT_KEYS.includes(k)) out[k] = v; else if (differs(v, s[k])) refused.push(k); }
    return { report: out, refused };
  }
  if (mode === 'sow_parent') {
    for (const k of Object.keys(inc)) if (k !== 'ubos' && differs(inc[k], s[k])) refused.push(k);
    const stored = Array.isArray(s.ubos) ? s.ubos : [];
    const target = resolveOwner(stored, interviewee);
    let stamp = null;
    for (const x of (Array.isArray(inc.ubos) ? inc.ubos : [])) {
      if (!x || typeof x !== 'object') continue;
      const u = resolveOwner(stored, x);
      if (!u) {
        // An entry that places on no single owner: an echo of an owner the rule
        // cannot tell apart changes nothing; anything else is refused.
        if (!stored.some(e => e && typeof e === 'object' && sameValue(e, x))) refused.push('ubos: an entry not on the case, or not one owner');
        continue;
      }
      for (const k of Object.keys(x)) if (!SOW_UBO_FIELDS.includes(k) && differs(x[k], u[k])) refused.push('ubos.' + k);
      const sowChanged = SOW_UBO_FIELDS.some(f => Object.prototype.hasOwnProperty.call(x, f) && differs(x[f], u[f]));
      if (u === target) stamp = x;
      else if (sowChanged) refused.push(target ? 'ubos: an owner this interview was not sent to' : 'ubos: the interviewed owner could not be identified on the case');
    }
    if (target && stamp) {
      out.ubos = stored.map((u) => {
        if (u !== target) return u;
        const next = { ...u };
        for (const f of SOW_UBO_FIELDS) if (Object.prototype.hasOwnProperty.call(stamp, f)) next[f] = stamp[f];
        return next;
      });
    }
    return { report: out, refused: [...new Set(refused)] };
  }
  for (const k of Object.keys(inc)) if (differs(inc[k], s[k])) refused.push(k);
  return { report: out, refused };
}
